import { useState } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  confirmPasswordReset,
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { ref, set, update } from 'firebase/database';
import { auth, db } from '../firebase';

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ログイン
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return true;
    } catch (err) {
      setError(getErrorMessage(err.code));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ログアウト
  const logout = async () => {
    await signOut(auth);
  };

  // パスワードリセット（再設定）メールを送信する
  const sendPasswordReset = async (email) => {
    setLoading(true);
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      return { ok: true };
    } catch (err) {
      const msg = err.code === 'auth/user-not-found'
        ? 'このメールアドレスは登録されていません。入力内容をご確認ください。'
        : getErrorMessage(err.code);
      setError(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // 招待URLからパスワードを設定する
  const confirmInvite = async (oobCode, newPassword, uid, profileData, email) => {
    setLoading(true);
    setError(null);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      if (uid) {
        // 既存DBレコードあり → 上書き保存
        await set(ref(db, `users/${uid}`), {
          ...profileData,
          created_at: profileData?.created_at ?? Date.now(),
        });
      } else if (email) {
        // DBレコードなし → ログインしてレコードを新規作成
        const cred = await signInWithEmailAndPassword(auth, email, newPassword);
        await set(ref(db, `users/${cred.user.uid}`), {
          email,
          role: 'owner',
          display_name: '',
          created_at: Date.now(),
          license: { plan: 'trial', status: 'active', expires_at: Date.now() + 30 * 24 * 60 * 60 * 1000 },
        });
      }
      return true;
    } catch (err) {
      setError(getErrorMessage(err.code));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ゲストアカウント新規登録（応募フォームから）
  const registerGuest = async (email, password, profileData) => {
    setLoading(true);
    setError(null);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await set(ref(db, `users/${cred.user.uid}`), {
        email,
        role: 'guest',
        display_name: profileData.vrchat_name || '',
        vrchat_name:    profileData.vrchat_name    || null,
        vrchat_profile: profileData.vrchat_profile || null,
        x_url:          profileData.x_url          || null,
        discord:        profileData.discord        || null,
        created_at: Date.now(),
        created_via: 'form',
      });
      return { uid: cred.user.uid };
    } catch (err) {
      const msg = getErrorMessage(err.code);
      setError(msg);
      throw new Error(err.code);
    } finally {
      setLoading(false);
    }
  };

  // プロフィール更新（VRChat情報・SNS）
  const updateUserProfile = async (uid, profileData) => {
    setLoading(true);
    setError(null);
    try {
      const updates = {};
      if (profileData.vrchat_name    !== undefined) updates.vrchat_name    = profileData.vrchat_name    || null;
      if (profileData.vrchat_profile !== undefined) updates.vrchat_profile = profileData.vrchat_profile || null;
      if (profileData.x_url          !== undefined) updates.x_url          = profileData.x_url          || null;
      if (profileData.discord        !== undefined) updates.discord        = profileData.discord        || null;
      if (profileData.display_name   !== undefined) updates.display_name   = profileData.display_name   || null;
      if (profileData.image_url      !== undefined) updates.image_url      = profileData.image_url      || null;
      await update(ref(db, `users/${uid}`), updates);
      return true;
    } catch (err) {
      setError('プロフィールの更新に失敗しました');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // パスワード変更（再認証あり）
  const changePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);
    try {
      const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);
      return { ok: true };
    } catch (err) {
      const msg = getPwErrorMessage(err.code);
      setError(msg);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  const getPwErrorMessage = (code) => {
    const messages = {
      'auth/wrong-password':       '現在のパスワードが正しくありません',
      'auth/invalid-credential':   '現在のパスワードが正しくありません',
      'auth/too-many-requests':    'ログイン試行が多すぎます。しばらく時間をおいてから再試行してください',
      'auth/weak-password':        '新しいパスワードは6文字以上で設定してください',
      'auth/requires-recent-login': 'セキュリティのため再ログインが必要です。一度ログアウトして再度ログインしてください',
    };
    return messages[code] ?? 'パスワードの変更に失敗しました。もう一度お試しください';
  };

  const getErrorMessage = (code) => {
    const messages = {
      'auth/invalid-credential': 'メールアドレスまたはパスワードが正しくありません',
      'auth/user-not-found': 'メールアドレスが見つかりません',
      'auth/wrong-password': 'パスワードが正しくありません',
      'auth/invalid-email': 'メールアドレスの形式が正しくありません',
      'auth/user-disabled': 'このアカウントは管理者により無効化されています',
      'auth/too-many-requests': 'ログイン失敗が続いたためブロックされました。しばらく時間をおいてから再試行してください',
      'auth/expired-action-code': '招待URLの有効期限が切れています',
      'auth/invalid-action-code': '招待URLが無効です'
    };
    return messages[code] ?? 'ログインに失敗しました。通信状況などを確認し、もう一度お試しください。';
  };

  return { login, logout, confirmInvite, sendPasswordReset, registerGuest, updateUserProfile, changePassword, loading, error };
};
